/**
 * Admin Referral View
 *
 * Displays referral leaderboard and management tools.
 * Only accessible to super admin (ryan@meetwithreps.com)
 */

import React, { useState, useEffect } from 'react';
import {
  Trophy, Users, Mail, CheckCircle, Clock, DollarSign,
  ChevronDown, ChevronUp, Calendar, TrendingUp, Gift, RefreshCw
} from 'lucide-react';
import referralService from '../services/referralService';
import { logger } from '../utils/logger';

const AdminReferralView = ({ currentUser }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [allReferrals, setAllReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [expandedRep, setExpandedRep] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Generate month options for the last 12 months
  const getMonthOptions = () => {
    const options = [{ value: '', label: 'All Time' }];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7); // YYYY-MM
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }

    return options;
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [leaderboardData, referralsData] = await Promise.all([
        referralService.getAdminLeaderboard(selectedMonth || null),
        referralService.getAllReferralsForAdmin({ month: selectedMonth || null })
      ]);

      setLeaderboard(leaderboardData);
      setAllReferrals(referralsData);
    } catch (error) {
      logger.error('Error loading admin referral data', { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedMonth]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleMarkPaid = async (referralId) => {
    try {
      setProcessingPayment(true);

      await referralService.markRewardPaid(
        referralId,
        paymentAmount ? parseFloat(paymentAmount) : null,
        paymentNotes || null
      );

      // Refresh data
      await loadData();

      // Close modal
      setShowPaymentModal(null);
      setPaymentAmount('');
      setPaymentNotes('');
    } catch (error) {
      logger.error('Error marking reward paid', { error: error.message });
      alert('Failed to mark reward as paid. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate summary stats
  const summaryStats = {
    totalInvitations: allReferrals.length,
    totalConversions: allReferrals.filter(r => r.converted).length,
    pendingRewards: allReferrals.filter(r => r.reward_eligible && !r.reward_paid).length,
    paidRewards: allReferrals.filter(r => r.reward_paid).length,
    conversionRate: allReferrals.length > 0
      ? Math.round((allReferrals.filter(r => r.converted).length / allReferrals.length) * 100)
      : 0
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading referral data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Trophy className="w-7 h-7 mr-2 text-yellow-500" />
            Referral Leaderboard
          </h1>
          <p className="text-gray-600 mt-1">
            Track sales rep invitations and customer conversions
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Month Filter */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {getMonthOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Mail className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.totalInvitations}</p>
              <p className="text-sm text-gray-600">Invitations Sent</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.totalConversions}</p>
              <p className="text-sm text-gray-600">Conversions</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-purple-500 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.conversionRate}%</p>
              <p className="text-sm text-gray-600">Conversion Rate</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-orange-500 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.pendingRewards}</p>
              <p className="text-sm text-gray-600">Pending Rewards</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Gift className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.paidRewards}</p>
              <p className="text-sm text-gray-600">Rewards Paid</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Sales Rep Rankings</h2>
        </div>

        {leaderboard.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No referrals found for this period.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {leaderboard.map((rep, index) => (
              <div key={rep.referrer_id}>
                {/* Rep Row */}
                <div
                  className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    expandedRep === rep.referrer_id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setExpandedRep(expandedRep === rep.referrer_id ? null : rep.referrer_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {/* Rank */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-200 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-600' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Rep Info */}
                      <div>
                        <p className="font-semibold text-gray-900">{rep.referrer_name}</p>
                        <p className="text-sm text-gray-500">{rep.referrer_email}</p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{rep.total_invitations}</p>
                        <p className="text-xs text-gray-500">Invites</p>
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{rep.total_conversions}</p>
                        <p className="text-xs text-gray-500">Converts</p>
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-bold text-purple-600">{rep.conversion_rate}%</p>
                        <p className="text-xs text-gray-500">Rate</p>
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-bold text-orange-600">{rep.pending_rewards}</p>
                        <p className="text-xs text-gray-500">Pending</p>
                      </div>

                      {/* Expand/Collapse Icon */}
                      {expandedRep === rep.referrer_id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Referral Details */}
                {expandedRep === rep.referrer_id && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Referral Details</h4>
                    <div className="space-y-2">
                      {rep.referrals.map((referral) => (
                        <div
                          key={referral.id}
                          className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="font-medium text-gray-900">
                                {referral.invitee_name || 'Unknown Provider'}
                              </p>
                              {referral.converted && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  Converted
                                </span>
                              )}
                              {referral.reward_paid && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                  Paid {referral.reward_amount ? `$${referral.reward_amount}` : ''}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{referral.invitee_email}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Invited: {formatDate(referral.invitation_sent_at)}
                              {referral.converted_at && ` | Converted: ${formatDate(referral.converted_at)}`}
                            </p>
                          </div>

                          {/* Action Button */}
                          {referral.reward_eligible && !referral.reward_paid && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPaymentModal(referral.id);
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center"
                            >
                              <DollarSign className="w-4 h-4 mr-1" />
                              Mark Paid
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mark Reward as Paid</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Amount (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Gift card sent via email"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPaymentModal(null);
                  setPaymentAmount('');
                  setPaymentNotes('');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleMarkPaid(showPaymentModal)}
                disabled={processingPayment}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {processingPayment ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferralView;
